import { atom } from 'jotai';
import { atomWithDefault, loadable } from 'jotai/utils';
import { withAtomEffect } from 'jotai-effect';
import { atomFamily } from 'jotai-family';
import * as Sentry from '@sentry/react';
import { dequal } from 'dequal/lite';

import { atomFamilyWithDefault, createSimpleState, createState } from '@rewardopl/jotai';
import { get as networkGet } from '@rewardopl/utils/network';
import { stringify } from '@rewardopl/utils/url';

import { APP_ID } from './constants';

import { PROD } from './env';

import type {
  ActiveCardSubscription,
  Business,
  Card,
  CardSubscription,
  Challenge,
  Config,
  CouponActivation,
  CouponWithDynamicProperties,
  HeavilyFilteredUser,
  Message,
  NotRedeemedRewardTransaction,
  Offer,
  PlaceWithDynamicPropertiesForUser,
  ProductWithDynamicPropertiesForUser,
  RewardTransaction,
  RewardWithDynamicProperties,
  Transaction,
  UnreadMessage,
  User,
} from '@rewardopl/types';
import type { Atom, SetStateAction } from 'jotai';

type ChallengeRanking = { user: HeavilyFilteredUser; score: number }[];

export const configState = atom(
  () =>
    networkGet('/api/config', {
      headers: {
        'app-id': APP_ID,
      },
    }) as Promise<Config>,
);

export const configLoginState = atom(async (get) => {
  const config = await get(configState);

  if (!config.login) {
    throw new Error('Login config not found');
  }

  return config.login;
});

export const configLoginLoadable = loadable(configLoginState);

function syncUser(user: User | null) {
  if (PROD) {
    Sentry.setUser(user ? { id: user._id, email: user.email } : null);
  }

  localStorage.setItem('user', JSON.stringify(user));
}

async function getUser(signal?: AbortSignal): Promise<User | null> {
  let response = null;
  try {
    response = await (networkGet('/api/users/current', { signal }) as Promise<User>);
  } catch {}

  syncUser(response);

  return response;
}

const userWasCached = Boolean(localStorage.getItem('user'));

function readCachedUser(): User | null | undefined {
  const rawLocalStorageValue = localStorage.getItem('user');

  if (rawLocalStorageValue) {
    try {
      return JSON.parse(rawLocalStorageValue) as User | null;
    } catch {}
  }

  return undefined;
}

const defaultMaybeCurrentUserBaseAtom = atomWithDefault<User | null | Promise<User | null>>(() => {
  const cached = readCachedUser();

  if (cached !== undefined) {
    return cached;
  }

  return getUser();
});

const defaultMaybeCurrentUserState = withAtomEffect(
  defaultMaybeCurrentUserBaseAtom,
  (_get, set) => {
    // Only refetch if user was initially in localStorage, therefore potentially stale
    if (!userWasCached) {
      return;
    }

    const cached = readCachedUser();

    const controller = new AbortController();

    const { signal } = controller;

    getUser(signal).then((nextValue) => {
      if (signal.aborted) {
        return;
      }

      if (!dequal(nextValue, cached)) {
        set(defaultMaybeCurrentUserBaseAtom, nextValue);
      }
    });

    return () => {
      controller.abort();
    };
  },
);

export const maybeCurrentUserState = atom<
  User | null | Promise<User | null>,
  [SetStateAction<User | null>],
  void
>(
  (get) => get(defaultMaybeCurrentUserState),
  async (get, set, nextValueOrSetter: SetStateAction<User | null>) => {
    const nextValue =
      typeof nextValueOrSetter === 'function'
        ? nextValueOrSetter(await get(defaultMaybeCurrentUserState))
        : nextValueOrSetter;

    localStorage.setItem('user', JSON.stringify(nextValue));

    set(defaultMaybeCurrentUserState, nextValue);
  },
);

export const maybeCurrentUserLoadable = loadable(maybeCurrentUserState);

export const currentUserState = atom(
  async (get) => {
    const maybeUser = await get(maybeCurrentUserState);

    if (!maybeUser) {
      throw new Error('User not found');
    }

    return maybeUser;
  },
  (_get, set, nextValue: User) => {
    set(maybeCurrentUserState, nextValue);
  },
);

export const currentUserIdState = atom(async (get) => (await get(currentUserState))._id);

export const maybeBusinessQuery = atomFamily((id: string | undefined) =>
  atom(() => (id ? (networkGet(`/api/businesses/${id}`) as Promise<Business>) : null)),
);

export const businessQuery = atomFamily(
  (id: string) => maybeBusinessQuery(id) as Atom<Promise<Business>>,
);

export const businessCardsQuery = atomFamily((id: string) =>
  atom(() => networkGet(`/api/businesses/${id}/cards`) as Promise<Card[]>),
);

export const businessPlacesQuery = atomFamily(
  (params: {
    businessId: Business['_id'];
    includePlacesWithoutLocation?: boolean;
    lat?: number;
    lng?: number;
    limit?: number;
  }) => {
    const { businessId: id, ...rest } = params;

    return atom(
      () =>
        networkGet(`/api/businesses/${id}/places${stringify(rest)}`) as Promise<
          (PlaceWithDynamicPropertiesForUser & { distance: number })[]
        >,
    );
  },
  dequal,
);

export const cardQuery = atomFamily((id: string) =>
  atom(() => networkGet(`/api/cards/${id}`) as Promise<Card>),
);

export const cardConfigQuery = atomFamily((id: string) =>
  atom(() => networkGet(`/api/cards/${id}/config`) as Promise<Partial<Config>>),
);

export const { itemsAtom: cardSubscriptionsState, itemAtomFamily: cardSubscriptionQuery } =
  createSimpleState<CardSubscription, CardSubscription['_id']>(
    () => networkGet('/api/users/current/card_subscriptions') as Promise<CardSubscription[]>,
    (id) => () =>
      networkGet(`/api/users/current/card_subscriptions/${id}`) as Promise<CardSubscription>,
  );

export const { itemsAtom: challengesState, itemAtomFamily: challengeQuery } = createSimpleState<
  Challenge,
  Challenge['_id']
>(
  () => networkGet('/api/challenges') as Promise<Challenge[]>,
  (id) => () => networkGet(`/api/challenges/${id}`) as Promise<Challenge>,
);

export const challengeRankingQuery = atomFamilyWithDefault(
  (id) => () => networkGet(`/api/challenges/${id}/ranking`) as Promise<ChallengeRanking>,
);

export const { itemsAtomFamily: cardChallengesQuery, itemAtomFamily: cardChallengeQuery } =
  createState(
    (id) => () => networkGet(`/api/cards/${id}/challenges`) as Promise<Challenge[]>,
    ([baseId, id]) =>
      () =>
        networkGet(`/api/cards/${baseId}/challenges/${id}`) as Promise<Challenge>,
  );

export const { itemsAtomFamily: cardCouponsQuery, itemAtomFamily: cardCouponQuery } = createState(
  (id) => () => networkGet(`/api/cards/${id}/coupons`) as Promise<CouponWithDynamicProperties[]>,
  ([baseId, id]) =>
    () =>
      networkGet(`/api/cards/${baseId}/coupons/${id}`) as Promise<CouponWithDynamicProperties>,
);

const defaultCardCouponActivationsQuery = atomFamily(
  ([baseId, id]: [string, string]) =>
    atomWithDefault<CouponActivation[] | Promise<CouponActivation[]>>(
      () =>
        networkGet(`/api/cards/${baseId}/coupons/${id}/coupon_activations`) as Promise<
          CouponActivation[]
        >,
    ),
  dequal,
);

export const cardCouponActivationsQuery = atomFamily((params: [string, string]) =>
  atom(
    (get) => get(defaultCardCouponActivationsQuery(params)),
    (_get, set, nextValueOrSetter: SetStateAction<CouponActivation[]>) => {
      set(
        defaultCardCouponActivationsQuery(params),
        typeof nextValueOrSetter === 'function'
          ? async (prevValueOrPromise) => {
              const prevValue = await prevValueOrPromise;

              return nextValueOrSetter(prevValue);
            }
          : nextValueOrSetter,
      );
    },
  ),
);

export const { itemsAtomFamily: cardOffersQuery, itemAtomFamily: cardOfferQuery } = createState(
  (id) => () => networkGet(`/api/cards/${id}/offers`) as Promise<Offer[]>,
  ([baseId, id]) =>
    () =>
      networkGet(`/api/cards/${baseId}/offers/${id}`) as Promise<Offer>,
);

export const { itemsAtomFamily: cardRewardsQuery, itemAtomFamily: cardRewardQuery } = createState(
  (id) => () => networkGet(`/api/cards/${id}/rewards`) as Promise<RewardWithDynamicProperties[]>,
  ([baseId, id]) =>
    () =>
      networkGet(`/api/cards/${baseId}/rewards/${id}`) as Promise<RewardWithDynamicProperties>,
);

export const { itemsAtom: messagesState, itemAtomFamily: messageQuery } = createSimpleState<
  Message,
  Message['_id']
>(
  () => networkGet('/api/users/current/messages') as Promise<Message[]>,
  (id) => () => networkGet(`/api/users/current/messages/${id}`) as Promise<Message>,
);

export const placesQuery = atomFamily(
  (params: {
    categories: string;
    includePlacesWithoutLocation?: boolean;
    lat?: number;
    limit?: number;
    lng?: number;
    search: string;
  }) =>
    atom(
      () =>
        networkGet(`/api/places${stringify(params)}`) as Promise<
          PlaceWithDynamicPropertiesForUser[]
        >,
    ),
  dequal,
);

export const placeQuery = atomFamilyWithDefault(
  (id: string) => () =>
    networkGet(`/api/places/${id}`) as Promise<PlaceWithDynamicPropertiesForUser>,
);

export const { itemsAtomFamily: placeProductsQuery, itemAtomFamily: placeProductQuery } =
  createState(
    (id) => () =>
      networkGet(`/api/places/${id}/products`) as Promise<ProductWithDynamicPropertiesForUser[]>,
    ([baseId, id]) =>
      () =>
        networkGet(
          `/api/places/${baseId}/products/${id}`,
        ) as Promise<ProductWithDynamicPropertiesForUser>,
  );

export const { itemsAtomFamily: transactionsQuery, itemAtomFamily: transactionQuery } = createState(
  (param: string | { id: string; offset: number; limit: number }) => () => {
    const { id, ...rest } = typeof param === 'string' ? { id: param } : param;

    return networkGet(`/api/card_subscriptions/${id}/transactions${stringify(rest)}`) as Promise<
      Transaction[]
    >;
  },
  ([baseId, id]) =>
    () =>
      networkGet(`/api/card_subscriptions/${baseId}/transactions/${id}`) as Promise<Transaction>,
);

function isActiveCardSubscription(
  cardSubscription: CardSubscription,
): cardSubscription is ActiveCardSubscription {
  return cardSubscription.active;
}

export const activeCardSubscriptionsState = atom(async (get) =>
  (await get(cardSubscriptionsState)).filter(isActiveCardSubscription),
);

export const cardSubscriptionBalanceQuery = atomFamily((id: CardSubscription['_id']) =>
  atom(async (get) => {
    const cardSubscription = await get(cardSubscriptionQuery(id));

    return cardSubscription.balance;
  }),
);

function getIsRewardTransaction(transaction: Transaction): transaction is RewardTransaction {
  return transaction.type === 'reward';
}

function getIsRedeemableTransaction(
  transaction: Transaction,
): transaction is NotRedeemedRewardTransaction {
  return getIsRewardTransaction(transaction) && !transaction.redeemed && !transaction.returned;
}

export const redeemableTransactionsQuery = atomFamily((id: string) =>
  atom(
    async (get) => {
      const transactions = await get(transactionsQuery(id));

      return transactions.filter(getIsRedeemableTransaction);
    },
    (_get, set, nextValueOrGetter: SetStateAction<NotRedeemedRewardTransaction[]>) => {
      set(transactionsQuery(id), (prevValue) => {
        const nextValue =
          typeof nextValueOrGetter === 'function'
            ? nextValueOrGetter(prevValue.filter(getIsRedeemableTransaction))
            : nextValueOrGetter;

        return prevValue.filter((transaction) =>
          getIsRedeemableTransaction(transaction)
            ? nextValue.find((nextTransaction) => transaction._id === nextTransaction._id)
            : transaction,
        );
      });
    },
  ),
);

function isUnreadMessage(message: Message): message is UnreadMessage {
  return !message.is_read;
}

export const unreadMessagesCountQuery = atom(async (get) => {
  const messages = await get(messagesState);

  return messages.filter(isUnreadMessage).length;
});

export const unreadMessagesCountLoadable = loadable(unreadMessagesCountQuery);

export const pendingCouponRefreshesState = atom<Card['_id'][]>([]);

export const pendingRewardRefreshesState = atom<Card['_id'][]>([]);
